import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import StaticLevelPreview from "@/components/Elements/FieldPreview/StaticLevelPreview";
import Filters, { useStaticStatesFilter } from "@/components/Elements/Filters";
import ModelList from "@/components/Elements/ModelList";
import {
  TableCellComponent,
  TableCellMaterialInfo,
  TableCellRiskMatrix,
} from "@/components/Elements/ObservationTableCell";
import { getPageModalBackgroundList } from "@/components/Elements/PageModalNavigation";
import TableTitleCell from "@/components/Elements/TableTitleCell";
import ObservationRecommendedInterventionsCell from "@/components/Observation/ObservationRecommendedInterventionsCell";
import { REFERENTIAl_STATES_ENDPOINTS } from "@/entities/referential/referential-states";
import { useListContext } from "@/modules/list";
import ListTable, { filterListTableColumns } from "@/modules/list-table";
import ObservationRiskTag from "@/modules/observation/observation-risk-tag";
import { useObservationsTableBatchEdit } from "@/modules/observations/observations-table";
import { StateTag } from "@/modules/state-tag";
import {
  getTableFilterQueryable,
  TABLE_FILTER_TEXT,
  useObservationsTableFilterRisk,
  useTableFilterReferentialStates,
} from "@/modules/table-filter";
import { format } from "@/utilities/date";

const ObservationsTable = ({ hiddenColumns = [], ...listTableProps }) => {
  const { t } = useTranslation();
  const riskFilter = useObservationsTableFilterRisk();
  const { data } = useListContext();
  const { onBatchEditClick } = useObservationsTableBatchEdit();

  const interventionStatesFilter = useTableFilterReferentialStates(
    REFERENTIAl_STATES_ENDPOINTS.INTERVENTION
  );
  const observationStatesFilter = useTableFilterReferentialStates(
    REFERENTIAl_STATES_ENDPOINTS.OBSERVATION
  );
  const recommendationStatesFilter = useStaticStatesFilter("recommendation");

  const getObservationLink = (id) => ({
    to: `/observations/${id}`,
    modalState: { backgroundList: getPageModalBackgroundList(data) },
    inModal: true,
  });

  const columns = [
    {
      dataIndex: "name",
      title: t("observation:name"),
      render: (name, { id }) => (
        <TableTitleCell text={name} link={getObservationLink(id)} />
      ),
      sorter: true,
      fixed: "left",
      minWidth: 220,
      ...TABLE_FILTER_TEXT,
    },
    {
      dataIndex: "id",
      title: t("observation:id"),
      render: (id) => (
        <TableTitleCell text={`${id}`} link={getObservationLink(id)} />
      ),
      sorter: true,
      ...TABLE_FILTER_TEXT,
    },
    {
      dataIndex: "state",
      title: t("global:status"),
      render: (state) => <StateTag value={state} type="observation" />,
      sorter: true,
      ...observationStatesFilter,
    },
    {
      dataIndex: "risk",
      title: t("observation:risk:label"),
      render: (_, observation) => (
        <ObservationRiskTag observation={observation} />
      ),
      key: "risk_level",
      sorter: true,
      ...riskFilter,
    },
    {
      dataIndex: "observation_date",
      title: t("observation:observationDateColumn"),
      render: (observation_date) => format(observation_date),
      sorter: true,
      ...Filters.dateRange,
    },
    {
      title: t("observation:concernedAsset"),
      dataIndex: "asset",
      render: (asset) => (
        <TableTitleCell text={asset?.name} link={`/assets/${asset?.id}`} />
      ),
      sorter: true,
      sorterKey: "asset.name",
      key: "asset.id",
      ...getTableFilterQueryable({ model: "assets" }),
    },
    {
      dataIndex: ["asset", "asset_type"],
      title: t("observation:assetType"),
      render: ({ name }) => <TableTitleCell text={name} />,
      key: "asset.asset_type_id",
      ...getTableFilterQueryable({ model: "assetTypes" }),
    },
    {
      dataIndex: "source_intervention",
      title: t("observation:sourceIntervention"),
      render: (intervention) => (
        <TableTitleCell
          text={intervention?.name}
          link={`/interventions/${intervention?.id}`}
        />
      ),
      sorter: true,
      ...getTableFilterQueryable({ model: "interventions" }),
    },
    {
      dataIndex: ["source_intervention", "state"],
      title: t("observation:sourceInterventionStateColumn"),
      render: (state) => <StateTag value={state} type="intervention" />,
      key: "sourceIntervention.state",
      ...interventionStatesFilter,
    },
    {
      dataIndex: "recommendation_state",
      title: t("observation:recommendedInterventions.title"),
      render: (_progress, observation) => (
        <ObservationRecommendedInterventionsCell observation={observation} />
      ),
      ...recommendationStatesFilter,
    },
    {
      dataIndex: ["severity"],
      title: t("observation:severity"),
      render: (severity) => (
        <StaticLevelPreview field="severity" level={severity} />
      ),
      key: "severity_level",
      ...Filters.staticLevelFilter("severity"),
    },
    {
      dataIndex: ["source", "component"],
      title: t("observation:source.component"),
      render: (_component, observation) => (
        <TableCellComponent observation={observation} />
      ),
    },
    {
      dataIndex: ["source", "material"],
      title: t("observation:source.material"),
      render: (_material, observation) => (
        <TableCellMaterialInfo observation={observation} field="material" />
      ),
    },
    {
      dataIndex: ["source", "defect"],
      title: t("observation:source.defect"),
      render: (_defect, observation) => (
        <TableCellMaterialInfo observation={observation} field="defect" />
      ),
    },
    {
      dataIndex: ["source", "probability"],
      title: t("observation:source.probability"),
      render: (_probability, observation) => (
        <TableCellRiskMatrix observation={observation} field="probability" />
      ),
    },
    {
      dataIndex: ["source", "consequence"],
      title: t("observation:source.consequence"),
      render: (_consequence, observation) => (
        <TableCellRiskMatrix observation={observation} field="consequence" />
      ),
    },
    {
      dataIndex: ["source_intervention", "users"],
      title: t("observation:users"),
      render: (users) => <ModelList model="user" values={users} />,
    },
  ];

  const filtredColumns = filterListTableColumns({ columns, hiddenColumns });

  return (
    <ListTable
      columns={filtredColumns}
      onBatchEditClick={onBatchEditClick}
      {...listTableProps}
    />
  );
};

ObservationsTable.propTypes = {
  hiddenColumns: PropTypes.array,
};

export default ObservationsTable;
